/**
 * K.H. Global Animation System
 * 
 * Automatically animates sections and elements on scroll using IntersectionObserver.
 * Supports multiple animation types with staggered timing for a premium feel.
 * 
 * Usage: Add data-animate="animation-type" to any element
 * Optional: Add data-delay="100" for custom stagger timing (in ms)
 */

(function () {
    'use strict';

    // Configuration
    const CONFIG = {
        threshold: 0.15,           // Trigger when 15% of element is visible
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before element enters viewport
        once: true                 // Animate only once (don't re-trigger on scroll up)
    };

    // Store observed elements to prevent re-animation
    const animatedElements = new WeakSet();

    /**
     * Initialize the animation system
     */
    function init() {
        // Check if IntersectionObserver is supported
        if (!('IntersectionObserver' in window)) {
            console.warn('IntersectionObserver not supported. Animations disabled.');
            // Fallback: show all elements immediately
            document.querySelectorAll('[data-animate]').forEach(el => {
                el.classList.add('is-visible');
            });
            return;
        }

        // Create observer
        const observer = new IntersectionObserver(handleIntersection, {
            threshold: CONFIG.threshold,
            rootMargin: CONFIG.rootMargin
        });

        // Observe all elements with data-animate attribute
        const elements = document.querySelectorAll('[data-animate]');
        elements.forEach(element => {
            observer.observe(element);
        });
    }

    /**
     * Handle intersection events
     */
    function handleIntersection(entries, observer) {
        entries.forEach(entry => {
            // Only animate when entering viewport
            if (entry.isIntersecting) {
                const element = entry.target;

                // Skip if already animated
                if (animatedElements.has(element)) {
                    return;
                }

                // Apply animation
                animateElement(element);

                // Mark as animated
                animatedElements.add(element);

                // Stop observing if once is true
                if (CONFIG.once) {
                    observer.unobserve(element);
                }
            }
        });
    }

    /**
     * Animate a single element
     */
    function animateElement(element) {
        // Get custom delay if specified
        const delay = element.getAttribute('data-delay');

        if (delay) {
            // Apply delay
            setTimeout(() => {
                element.classList.add('is-visible');
            }, parseInt(delay, 10));
        } else {
            // No delay, animate immediately
            element.classList.add('is-visible');
        }
    }

    /**
     * Auto-initialize when DOM is ready
     */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        // DOM already loaded
        init();
    }

    // Expose API for manual control (optional)
    window.KHAnimations = {
        init: init,
        version: '1.0.0'
    };

})();
